<template>
  <v-container>
    <div id="settings-page-title">
      <h2 class="font-weight-bold">
        {{ $t('wallet.settings.page-title') }}
      </h2>
    </div>
    <v-card
      id="settings-name"
      class="mt-5 px-10"
    >
      <v-row>
        <v-col
          cols="12"
          sm="4"
        >
          <wallet-text-section
            :title="nameSectionTitle"
            :text="nameSectionText"
          />
        </v-col>
        <v-col
          cols="12"
          sm="8"
        >
          <v-card-text class="wallet-text">
            {{ $t('wallet.settings.name-not-setup') }}
          </v-card-text>
        </v-col>
      </v-row>
      <v-divider />
      <v-row>
        <v-col
          cols="12"
          sm="4"
        >
          <wallet-text-section
            :title="emailSectionTitle"
            :text="emailSectionText"
          />
        </v-col>
        <v-col
          cols="12"
          sm="8"
        >
          <v-card-text
            id="primary-email"
            class="wallet-text"
          >
            {{ primaryEmail }}
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>
    <v-card
      id="settings-emails"
      class="mt-5 px-10"
    >
      <v-row>
        <v-col
          cols="12"
          sm="4"
        >
          <wallet-text-section
            :title="rewardEmailsSectionTitle"
            :text="rewardEmailsSectionText"
          />
        </v-col>
        <v-col
          id="RL-email-addresses"
          cols="12"
          sm="8"
          class="pt-0"
        >
          <div
            v-for="email in recipientEmails"
            :key="email"
          >
            <v-row>
              <v-col>
                <v-card-text
                  :id="'email-address-' + recipientEmails.indexOf(email)"
                  class="wallet-text"
                >
                  {{ email }}
                </v-card-text>
              </v-col>
              <v-col />
            </v-row>
            <v-divider />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <v-card
      id="settings-delete"
      class="mt-5 px-10"
    >
      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <wallet-text-section
            :title="deleteSectionTitle"
            :text="deleteSectionText"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-dialog
            v-model="deleteWalletDialog"
            max-width="640"
          >
            <template #activator="{ on, attrs }">
              <tc-v-btn
                id="delete-acct-dialog-launcher"
                depressed
                outlined
                rounded
                large
                block
                color="error"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('wallet.settings.delete-wallet-verbiage') }}
              </tc-v-btn>
            </template>

            <v-card>
              <v-card-title class="headline lighten-2">
                Are you sure you want to delete your wallet?
              </v-card-title>

              <v-card-text>
                {{ $t('wallet.settings.delete-wallet-disclaimer') }}<br>
                {{ $t('wallet.settings.wallet-stored-pii-verbiage') }}
                <router-link
                  :aria-label="$t('home')"
                  :to="{ name: piiRouteName }"
                >
                  {{
                    $t('wallet.settings.wallet-stored-pii-verbiage-cont')
                  }}
                </router-link>.
              </v-card-text>

              <v-card-actions>
                <v-col class="text-right">
                  <tc-v-btn
                    id="cancel-delete-acct-button"
                    outlined
                    @click="deleteWalletDialog = false"
                  >
                    Cancel
                  </tc-v-btn>
                  <tc-v-btn
                    id="delete-acct-button"
                    outlined
                    color="error"
                    :loading="isDeleting"
                    :disabled="isDeleting"
                    @click="deleteRecipient"
                  >
                    {{ $t('wallet.settings.delete-wallet-verbiage') }}
                  </tc-v-btn>
                </v-col>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import WalletTextSection from '@/components/wallet/WalletTextSection'
import { mapGetters, mapActions } from 'vuex'
import { GET_RECIPIENT_DETAILS } from '@/store/get-types'
import { DELETE_RECIPIENT_WALLET } from '@/store/action-types'
import { DEFAULT_ROUTE } from '@/router/routes/route-names'
import { TcVBtn } from '@tangocard/mango'
import { getNonExistingRouteNameEquivalent } from '@/router/routes/utilities'
export default {
  name: 'WalletSettings',
  components: { WalletTextSection, TcVBtn },
  data () {
    return {
      deleteWalletDialog: false,
      isDeleting: false
    }
  },
  computed: {
    ...mapGetters({
      recipientDetails: GET_RECIPIENT_DETAILS
    }),
    nameSectionTitle () {
      return this.$t('wallet.settings.name-title')
    },
    nameSectionText () {
      return this.$t('wallet.settings.name-subtitle')
    },
    emailSectionTitle () {
      return this.$t('wallet.settings.account-email-title')
    },
    emailSectionText () {
      return this.$t('wallet.settings.account-email-subtitle')
    },
    rewardEmailsSectionTitle () {
      return this.$t('wallet.settings.reward-emails-title')
    },
    rewardEmailsSectionText () {
      return this.$t('wallet.settings.reward-emails-subtitle')
    },
    deleteSectionTitle () {
      return this.$t('wallet.settings.delete-title')
    },
    deleteSectionText () {
      return this.$t('wallet.settings.delete-subtitle')
    },
    piiRouteName () {
      return getNonExistingRouteNameEquivalent({ name: DEFAULT_ROUTE })
    },
    primaryEmail () {
      const primaryEmail = this.recipientDetails.emails.find(
        email => email.type === 'PRIMARY'
      )
      return primaryEmail ? primaryEmail.emailAddress : null
    },
    recipientEmails () {
      const emails = this.recipientDetails.emails.map(
        email => email.emailAddress
      )
      return emails || []
    }
  },
  methods: {
    ...mapActions({
      delete: DELETE_RECIPIENT_WALLET
    }),
    async deleteRecipient () {
      this.isDeleting = true
      await this.delete()
      this.$router.push({ name: DEFAULT_ROUTE })
      this.isDeleting = false
    }
  }
}
</script>

<style lang="scss" scoped></style>
